import { useLocation, matchPath } from 'react-router';
import { useRoleRoutes } from '@/routing/hooks/use-role-routes';
import { PageRoute } from '@/routing/types';

interface BreadcrumbItem {
  title: string;
  path: string | null;
}

export function useBreadcrumbs() {
  const location = useLocation();
  const { flattenedRoutes } = useRoleRoutes();

  // Find routes with actual path properties (exclude labels and groups without paths)
  const pageRoutes = flattenedRoutes.filter(
    (route): route is PageRoute => 'path' in route && !!route.path
  );

  // Function to replace dynamic parameters in route paths
  const replaceParams = (path: string, params: Record<string, string>) => {
    let result = path;
    Object.entries(params).forEach(([key, value]) => {
      result = result.replace(`:${key}`, value);
    });
    return result;
  };

  // Find the current route
  const currentRoute = pageRoutes.find((route) => {
    // Create a match function for the current path
    const match = matchPath(route.path, location.pathname);
    return !!match;
  });

  if (!currentRoute || !currentRoute.breadcrumb) {
    return [];
  }

  // Build breadcrumb chain
  const breadcrumbs: BreadcrumbItem[] = [];
  let current = currentRoute;
  const params: Record<string, string> = {};

  // Extract params from the current route
  const match = matchPath(current.path, location.pathname);
  if (match && match.params) {
    Object.entries(match.params).forEach(([key, value]) => {
      if (typeof value === 'string') {
        params[key] = value;
      }
    });
  }

  while (current && current.breadcrumb) {
    // Add current route to breadcrumbs
    breadcrumbs.unshift({
      title: current.breadcrumb.title,
      path: current.path === location.pathname ? null : replaceParams(current.path, params),
    });

    // Find parent route
    if (current.breadcrumb.parent) {
      const parentPath = replaceParams(current.breadcrumb.parent, params);
      const parentRoute = pageRoutes.find((route) => {
        // Find exact match or parameterized match
        return (
          route.path === parentPath ||
          (matchPath(route.path, parentPath) && route.path.includes(':'))
        );
      });

      if (parentRoute) {
        current = parentRoute;
      } else {
        current = null!;
      }
    } else {
      current = null!;
    }
  }

  return breadcrumbs;
}
